import { useRef, useState } from 'react';
import { serverErrorsOf } from '@/lib/formErrors';
import { editFailureOf } from '@/lib/editFailure';
import { showErrorToast } from '@/lib/toasts';

/** Moves the focus to the element with this id (the first invalid field after a submit). */
export const focusElement = (id: string) => document.getElementById(id)?.focus();

/** What the API said about a create form: per-field messages and a form-level one. */
interface CreateServerState {
  fields: Record<string, string>;
  form?: string;
}

/**
 * The state every "create" form shares: whether it was submitted (local errors show from then on), what the API
 * answered, and the idempotency key of the request (renewed once a request succeeded).
 */
export function useCreateFormState() {
  const [submitted, setSubmitted] = useState(false);
  const [server, setServer] = useState<CreateServerState>({ fields: {} });
  const key = useRef(crypto.randomUUID());
  return {
    submitted,
    server,
    idempotencyKey: () => key.current,
    renewKey: () => {
      key.current = crypto.randomUUID();
    },
    begin: () => {
      setSubmitted(true);
      setServer({ fields: {} });
    },
    /** The user is fixing a field the API complained about. */
    clearField: (field: string) => {
      if (server.fields[field]) setServer((s) => ({ ...s, fields: { ...s.fields, [field]: '' } }));
    },
    /** Shows what the API said (and the toast of a 5xx); returns the per-field messages. */
    fail: (err: unknown): Record<string, string> => {
      const mapped = serverErrorsOf(err);
      setServer({ fields: mapped.fields, form: mapped.form });
      if (mapped.toast) showErrorToast(mapped.toast);
      return mapped.fields;
    },
  };
}

/** The state every "edit" form shares: submitted, the API's field messages and the problem alert. */
export function useEditFormState(staleMessage: string, slugField?: string) {
  const [submitted, setSubmitted] = useState(false);
  const [problem, setProblem] = useState<unknown>(null);
  const [server, setServer] = useState<Record<string, string>>({});
  return {
    submitted,
    problem,
    server,
    begin: () => {
      setSubmitted(true);
      setServer({});
      setProblem(null);
    },
    /** The user is fixing a field the API complained about. */
    clearField: (field: string) => {
      if (server[field]) setServer((s) => ({ ...s, [field]: '' }));
    },
    fail: (err: unknown) => {
      const failure = editFailureOf(err, staleMessage, slugField);
      setServer(failure.server);
      setProblem(failure.problem);
      if (failure.toast) showErrorToast(failure.toast);
    },
  };
}
